import { alpha, Box, CardProps, Typography, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { ApexOptions } from "apexcharts";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactApexChart from "react-apexcharts";

import Card from "../../molecules/card/card";
import CardContent, {
  CardContentProps,
} from "../../molecules/card-content/card-content";

const ROW_LABEL_OVERLAY_BLEED = 4;

export interface HeatMapChartSeries {
  /** Row label, shown on the y-axis. */
  name: string;
  /** One cell per column; `x` is the column label, `y` the numeric cell value. */
  data: { x: string; y: number }[];
}

export interface HeatMapColorRange {
  /** Inclusive lower bound of the cell values this color applies to. */
  from: number;
  /** Inclusive upper bound of the cell values this color applies to. */
  to: number;
  /** Fill color for cells whose value falls within this range. */
  color: string;
  /** Text color for the data label of cells whose value falls within this range. */
  foreColor?: string;
  /** Label shown in the legend for this range. */
  name: string;
}

export interface HeatMapChartTotal {
  /** Row/column label this total belongs to (matches series `name`/data `x`). */
  name: string;
  /** Summed value for this row/column. */
  total: number;
}

export interface HeatMapChartCardProps {
  /** Global title of the card, displayed to the left. Ignored when `hideCard` is set. */
  title: string;
  /** Optional text rendered above the chart, e.g. to flag illustrative data. */
  subtitle?: ReactNode;
  /**
   * When true, renders only the chart (and `subtitle`), without its own
   * `Card`/`CardContent` chrome — for embedding inside a consumer's own
   * card component.
   */
  hideCard?: boolean;
  /** One series per row; each series' `data[].x` is a column category. */
  series: HeatMapChartSeries[];
  /** Discrete color bands mapped over the numeric `y` values. */
  colorScaleRanges: HeatMapColorRange[];
  /** Formats a cell's numeric value into the text shown inside it (defaults to the raw value). */
  dataLabelFormatter?: (value: number) => string;
  /** Whether to show the color-range legend below the chart. */
  showLegend?: boolean;
  /** Height of the chart in px. */
  height?: number;
  /**
   * Optional fixed chart width in px. When set, the chart renders at this
   * width inside a horizontally scrollable container instead of shrinking
   * to fit — for heat maps with many columns (e.g. one per OE) where cells
   * would otherwise become illegibly narrow.
   */
  width?: number;
  /**
   * Optional per-column sums. Rendered as a second line under each
   * matching column's x-axis label (e.g. `"AAMHO"` / `"12"`) instead of a
   * separate summary row — this keeps the total pixel-aligned with its
   * column since it's rendered by ApexCharts itself as part of the axis,
   * not a separately laid-out element.
   */
  columnTotals?: HeatMapChartTotal[];
  /**
   * Optional per-row sums. Rendered as a second line under each matching
   * row's y-axis label (e.g. `"Broker / intermediary"` / `"555"`) instead
   * of a separate summary list.
   */
  rowTotals?: HeatMapChartTotal[];
  /** Optional overall sum, rendered below the chart. */
  grandTotal?: number;
  /** (optional) more card specific props */
  cardProps?: CardProps;
  /** (optional) more card content specific props */
  cardContentProps?: CardContentProps;
}

export const HeatMapChartCard: PimoReactComponent<HeatMapChartCardProps> = ({
  title,
  subtitle,
  hideCard = false,
  series,
  colorScaleRanges,
  dataLabelFormatter,
  showLegend = true,
  height = 350,
  width,
  columnTotals,
  rowTotals,
  grandTotal,
  cardProps,
  cardContentProps,
}) => {
  const theme = useTheme();

  // See the `key` comment on `ReactApexChart` below: this must change
  // whenever the color bands, the series' row/column shape, OR any cell
  // value changes. `react-apexcharts` calls the chart's `updateSeries()`
  // (bypassing `updateOptions()` entirely) when only `series` differs
  // between renders — and ApexCharts' heatmap `updateSeries()` drops
  // `plotOptions.heatmap.colorScale.ranges` just as reliably as
  // `updateOptions()` does, leaving every cell's `fill` empty (rendered
  // black). Including each cell's value in the key forces a full remount
  // instead, which always renders correctly.
  const chartKey = [
    colorScaleRanges
      .map((range) => `${range.from}-${range.to}-${range.color}`)
      .join("|"),
    series
      .map((s) => `${s.name}:${s.data.map((cell) => cell.y).join(",")}`)
      .join("|"),
    // Totals are shown as a suffix on each row's/column's axis label (see
    // the `xaxis`/`yaxis` `labels.formatter`s below), so they must also be
    // part of this content signature — otherwise a totals-only change (with
    // row/column shape and colors unchanged) wouldn't recompute `options`
    // and the displayed totals would go stale.
    (columnTotals ?? []).map((t) => `${t.name}:${t.total}`).join("|"),
    (rowTotals ?? []).map((t) => `${t.name}:${t.total}`).join("|"),
  ].join("::");

  // `react-apexcharts` only re-checks whether it needs to call
  // `updateSeries`/`updateOptions` when the `series` (or `options`) prop
  // changes by *reference* (it's a `useEffect` dependency). Its own
  // "did series really change" check then compares our prop against
  // ApexCharts' own internal copy of the series — which always has an
  // extra `group` field ApexCharts adds itself (`{ name, data, group }`
  // vs our `{ name, data }`), so that comparison never matches and it
  // *always* concludes "series changed", forever, on every single
  // render where `series` is a new array — permanently triggering the
  // colorScale-dropping `updateSeries()` bug, no matter what the actual
  // data is. Keeping `series` referentially stable (same object as long
  // as `chartKey`, its content signature, hasn't changed) stops that
  // effect from firing at all for unrelated re-renders, so the broken
  // comparison never runs in the first place.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableSeries = useMemo(() => series, [chartKey]);

  // Shared by the yaxis label formatter (below) and the sticky row-label
  // overlay (further below), so both agree on each row's total.
  const rowTotalByName = useMemo(
    () => new Map((rowTotals ?? []).map((t) => [t.name, t.total])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartKey]
  );

  // `dataLabelFormatter` is a new function reference on every render (it's
  // typically an inline arrow from a card's `mapState`). `react-apexcharts`
  // deep-compares the whole `options` object it was given last render against
  // the new one to decide whether to call the chart's `updateOptions` — and
  // for functions that comparison falls back to reference equality, so an
  // unrelated re-render would otherwise always look like an options change.
  // Routing calls through a ref keeps one stable formatter identity so
  // unrelated re-renders don't spuriously trigger `updateOptions` (which, on
  // top of the unnecessary work, re-triggers ApexCharts' heatmap bug that
  // drops `colorScale.ranges` on that update path — see the `key` comment
  // below).
  const dataLabelFormatterRef = useRef(dataLabelFormatter);
  useEffect(() => {
    dataLabelFormatterRef.current = dataLabelFormatter;
  }, [dataLabelFormatter]);
  const stableDataLabelFormatter = useCallback(
    (value: number) => dataLabelFormatterRef.current?.(value) ?? String(value),
    []
  );
  const hasDataLabelFormatter = Boolean(dataLabelFormatter);

  const options: ApexOptions = useMemo(
    () => {
      const columnTotalByName = new Map(
        (columnTotals ?? []).map((t) => [t.name, t.total])
      );

      return {
        chart: {
          type: "heatmap",
          toolbar: { show: false },
          // We always pass an explicit `height`/`width` (this card doesn't
          // auto-fit to its container), so we don't need ApexCharts' own
          // resize-driven redraws — and its internal redraw path (triggered by
          // a `ResizeObserver` on the chart's *parent*, independent of React)
          // goes through the same `update()` call that drops
          // `colorScale.ranges` (see the `key` comment below). This card
          // mounts inside a tab/drag-and-drop layout whose surrounding
          // container is still settling its own size right after mount, which
          // reliably fires that parent-resize redraw once — corrupting the
          // colors before the user ever sees a correct render. Disabling both
          // keeps ApexCharts from redrawing itself outside of the props we
          // explicitly give it.
          redrawOnParentResize: false,
          redrawOnWindowResize: false,
        },
        dataLabels: {
          enabled: true,
          style: { colors: [theme.palette.text.primary] },
          formatter: hasDataLabelFormatter
            ? (value: number) => stableDataLabelFormatter(value)
            : undefined,
        },
        legend: { show: showLegend },
        plotOptions: {
          heatmap: {
            radius: 5,
            enableShades: false,
            colorScale: {
              ranges: colorScaleRanges,
            },
          },
        },
        grid: {
          padding: { left: 24, right: 0 },
        },
        xaxis: {
          type: "category",
          labels: {
            style: { colors: theme.palette.text.primary },
            trim: true,
            // Renders each column's total (if given) as a second line under
            // its axis label, instead of a separate totals strip below the
            // chart. ApexCharts draws each array entry as its own line.
            formatter: (value: string): string | string[] => {
              const total = columnTotalByName.get(value);
              return total === undefined ? value : [value, `${total}`];
            },
          },
        },
        yaxis: {
          labels: {
            style: { colors: theme.palette.text.primary },
            // Renders each row's total (if given) as a second line under its
            // axis label, instead of a separate totals list below the chart.
            // ApexCharts types this formatter as `(val: number) => string`
            // (its generic numeric-axis signature), but for a heatmap's
            // categorical y-axis it actually passes the row's series name
            // (a string) at runtime — `String(...)` below is a no-op in that
            // case and only guards against the declared type.
            formatter: (value: number): string | string[] => {
              const name = String(value);
              const total = rowTotalByName.get(name);
              return total === undefined ? name : [name, `${total}`];
            },
          },
        },
        stroke: {
          width: 4,
          colors: [theme.palette.background.paper],
        },
      };
    },
    // `chartKey` is a deliberate stand-in for `colorScaleRanges`/`series`/
    // `columnTotals`/`rowTotals` here: it's a stable, content-based
    // signature of all of them (see above), so depending on the actual
    // (identity-unstable) props directly would recompute `options` — and
    // hand react-apexcharts a new object — on every render instead of only
    // when the content truly changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chartKey,
      showLegend,
      hasDataLabelFormatter,
      stableDataLabelFormatter,
      theme.palette.text.primary,
      theme.palette.background.paper,
    ]
  );

  // ApexCharts renders the y-axis row labels as part of the same scrollable
  // SVG as the grid, so they scroll away with the rest of the chart — CSS
  // `position: sticky` can't pin them in place because ApexCharts positions
  // that SVG group with a `transform`, which sticky positioning doesn't
  // override. Instead, once the real labels have rendered, we measure their
  // on-screen position and draw our own plain-HTML duplicates in a `sticky`
  // overlay on top, which *does* stay pinned to the left edge while
  // scrolling (only relevant when horizontal scrolling is possible, i.e.
  // `width` is set).
  const chartWidthBoxRef = useRef<HTMLDivElement>(null);
  const [rowLabelOverlay, setRowLabelOverlay] = useState<{
    width: number;
    top: number;
    height: number;
    rows: { key: string; top: number; lines: string[] }[];
  } | null>(null);

  useEffect(() => {
    const container = chartWidthBoxRef.current;
    if (!width || !container) {
      // Deferred (not called synchronously in the effect body) so this
      // reset doesn't itself trigger a same-phase cascading render.
      const raf = requestAnimationFrame(() => setRowLabelOverlay(null));
      return () => cancelAnimationFrame(raf);
    }

    let cancelled = false;
    let raf = 0;
    let attempt = 0;
    // A little over 1s at 60fps — generous, since this only runs a handful
    // of times per chart render, but bounded so a chart that never settles
    // (e.g. genuinely 0 rows with a persistently mismatched DOM) doesn't
    // poll forever.
    const MAX_ATTEMPTS = 90;
    // ApexCharts always renders one extra, empty `<text>` alongside the
    // per-row labels in `.apexcharts-yaxis-texts-g` (confirmed by inspection
    // — an artifact of its own layout pass, unrelated to our data). Matching
    // on non-empty content instead of raw element count is what actually
    // lines these up with `series` one-to-one.
    const measure = () => {
      if (cancelled) {
        return;
      }
      const yaxisGroup =
        container.querySelector<SVGGElement>(".apexcharts-yaxis");
      const textEls = Array.from(
        container.querySelectorAll<SVGTextElement>(
          ".apexcharts-yaxis-texts-g text"
        )
      ).filter((el) => (el.textContent ?? "").trim().length > 0);
      if (!yaxisGroup || textEls.length !== series.length) {
        // ApexCharts renders the SVG asynchronously after mount/update, so
        // the label elements may not exist yet (or not all of them) on the
        // first few frames — keep retrying rather than giving up after one.
        attempt += 1;
        if (attempt < MAX_ATTEMPTS) {
          raf = requestAnimationFrame(measure);
        }
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const yaxisRect = yaxisGroup.getBoundingClientRect();
      const rows = textEls.map((el, index) => {
        const rect = el.getBoundingClientRect();
        const rowSeries = series[series.length - 1 - index];
        const lines = [rowSeries.name];
        const total = rowTotalByName.get(rowSeries.name);
        if (total !== undefined) {
          lines.push(`${total}`);
        }
        return {
          key: rowSeries.name,
          top: rect.top - containerRect.top + rect.height / 2,
          lines,
        };
      });
      const rowHeight =
        rows.length > 1 ? rows[1].top - rows[0].top : yaxisRect.height;

      setRowLabelOverlay({
        width: Math.ceil(yaxisRect.width) + 16,
        top: rows[0].top - rowHeight / 2,
        height: rowHeight * rows.length,
        rows,
      });
    };

    raf = requestAnimationFrame(measure);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [chartKey, width, series, rowTotalByName]);

  const content = (
    <>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: "block" }}
        >
          {subtitle}
        </Typography>
      )}
      {/*
       * `overflowX: auto` alone leaves `overflow-y` at its default, which the
       * CSS spec then also computes to `auto` (not `visible`) — so any
       * sub-pixel rounding between the chart's actual rendered height and our
       * computed `height` prop was enough to trigger a vertical scrollbar
       * too. Pinning `overflowY` to `hidden` guarantees only horizontal
       * scrolling is ever possible here, regardless of that.
       */}
      <Box sx={width ? { overflowX: "auto", overflowY: "hidden" } : undefined}>
        <Box
          ref={chartWidthBoxRef}
          sx={
            width ? { width, minWidth: width, position: "relative" } : undefined
          }
        >
          {rowLabelOverlay && (
            // A plain-HTML duplicate of the (still-rendered, now covered)
            // SVG row labels: `position: sticky` on a normal HTML element —
            // unlike on ApexCharts' `transform`-positioned SVG group — does
            // stay pinned to the scroll viewport's left edge, giving the
            // effect of "sticky" row labels while the heatmap itself scrolls
            // horizontally underneath. `height: 0` keeps it out of normal
            // flow (so it doesn't push the grand total below it down), with
            // the actual visible box positioned via its own `top`/`height`.
            <Box
              aria-hidden="true"
              sx={{
                position: "sticky",
                left: 0,
                top: 0,
                zIndex: 1,
                width: rowLabelOverlay.width,
                height: 0,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: rowLabelOverlay.top - ROW_LABEL_OVERLAY_BLEED,
                  left: 0,
                  width: rowLabelOverlay.width,
                  height: rowLabelOverlay.height + ROW_LABEL_OVERLAY_BLEED * 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(6px)",
                }}
              >
                {rowLabelOverlay.rows.map((row) => (
                  <Box
                    key={row.key}
                    sx={{
                      position: "absolute",
                      top:
                        row.top - rowLabelOverlay.top + ROW_LABEL_OVERLAY_BLEED,
                      left: 0,
                      right: 0,
                      transform: "translateY(-50%)",
                      px: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={theme.typography.fontWeightRegular}
                      noWrap
                    >
                      {row.lines[0]}
                    </Typography>
                    {row.lines[1] && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={theme.typography.fontWeightBold}
                        noWrap
                      >
                        {row.lines[1]}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <ReactApexChart
            // ApexCharts' heatmap silently drops `colorScale.ranges` when
            // `updateOptions` runs a plain re-render (e.g. once real,
            // data-driven ranges replace an initial empty/loading state) —
            // cells then keep whatever fill they last had, or none at all.
            // Keying on the ranges (and cell count, since row/column shape
            // changes hit the same bug) forces React to fully remount the
            // chart instead of relying on that broken update path.
            key={chartKey}
            className="apex-charts"
            options={options}
            series={stableSeries}
            type="heatmap"
            height={height}
            width={width}
          />
        </Box>
      </Box>
      {grandTotal !== undefined && (
        <Typography
          data-testid="HeatMapChartCard-grand-total"
          variant="body2"
          sx={{ mt: 2, fontWeight: 700 }}
        >
          Grand total: {new Intl.NumberFormat("en").format(grandTotal)}
        </Typography>
      )}
    </>
  );

  if (hideCard) {
    return <Box data-testid="HeatMapChartCard">{content}</Box>;
  }

  return (
    <Card {...cardProps} data-testid="HeatMapChartCard" title={title}>
      <CardContent {...cardContentProps}>{content}</CardContent>
    </Card>
  );
};

export default HeatMapChartCard;
